import React, { useEffect, useRef, useState } from 'react'
import styles from '@/modules/products/ProductDocumentPage/components/TableOfContent/tableOfContent.module.scss'
import { tocData } from '@/modules/products/ProductDocumentPage/components/TableOfContent/tableOfContent.model'
import TocMenuItem from '@/modules/products/ProductDocumentPage/components/TableOfContent/TocMenuItem/TocMenuItem'
import { throttle } from 'lodash'
import Link from 'next/link'
import { convertedDocumentLink } from '@/modules/products/ProductDocumentPage/utils/documentLinkConversion.utils';

/**
 * Props for the TableOfContent component.
 */
interface TableOfContentProps {
  items: tocData<string>[]
  hashValFromUrl: string
  tocTitle: string
  activeAnchor: string
  onItemClick: (e: React.MouseEvent<HTMLAnchorElement>, tocLink: string) => void
  maxDepth: number
  tocTopElement: string
}

/**
 * TableOfContent component to render a dynamic table of contents.
 * @param props - Props for the component.
 * @returns JSX.Element
 */
function TableOfContent({
  items,
  hashValFromUrl,
  tocTitle,
  activeAnchor,
  onItemClick,
  maxDepth,
  tocTopElement,
}: TableOfContentProps): JSX.Element {
  const [currentHeadingID, setCurrentHeadingID] = useState<string | null>(null)
  const listWrapperRef = useRef<HTMLDivElement>(null)

  /**
   * Scrolls to the anchor tag if the page URL contains a hash.
   */
  useEffect(() => {
    if (hashValFromUrl) {
      const scrollToAnchor = (): void => {
        const targetElement = document.getElementById(hashValFromUrl)
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
          setCurrentHeadingID(hashValFromUrl)
        }
      }

      // Delay the scroll to ensure the DOM is fully rendered
      setTimeout(scrollToAnchor, 500)
    }
  }, [hashValFromUrl])

  /**
   * Highlights the TOC item based on the current scroll position.
   */
  useEffect(() => {
    const handleScroll = (): void => {
      const headings = document.querySelectorAll('a.anchor[id]')
      let activeId: string | null = null

      headings.forEach((heading) => {
        const rect = heading.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        // Check if the heading is halfway up the screen
        if (rect.top >= 0 && rect.top <= viewportHeight / 2) {
          activeId = heading.getAttribute('id')
        }
      })

      // Update the active TOC link
      if (activeId && activeId !== currentHeadingID) {
        setCurrentHeadingID(activeId)

        // Scroll the active TOC link into view
        const activeElement = listWrapperRef.current?.querySelector(
          `li[data-id="${activeId}"]`
        )
        activeElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }

    const throttledHandleScroll = throttle(handleScroll, 100)

    window.addEventListener('scroll', throttledHandleScroll)

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll)
    }
  }, [currentHeadingID])

  return (
    <div className={styles.tableOfContentSectionWrapper} ref={listWrapperRef}>
      {items.length > 0 && (
        <div className={styles.tableOfContentSectionContent}>
          <p id="tableOfContentSectionTitle" className={styles.tableOfContentSectionTitle}>
            {tocTitle}
          </p>
          <ul className={styles.tableOfContentMenuList} role="list">
            <li>
              <Link href="#">
                <a
                  href={'#'}
                  id="heading-1"
                  data-testid={'tocLink'}
                  onClick={() => {
                    window.scrollTo(0, 0)
                  }}
                >
                  {tocTopElement}
                </a>
              </Link>
            </li>
            {items.map((item) => (
              <TocMenuItem
                key={item.id}
                item={item}
                maxDepth={maxDepth}
                activeAnchor={currentHeadingID || ''} // Highlight the active TOC link
                onItemClick={(e: React.MouseEvent<HTMLAnchorElement>, tocLink: string) => {
                  e.preventDefault()
                  const tocLinkElement = convertedDocumentLink(tocLink)
                  setCurrentHeadingID(tocLinkElement)
                  const targetHeading = document.getElementById(tocLinkElement)
                  targetHeading?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
              />
            ))}
          </ul>
        </div>
      )}  
    </div>
  )
}

export default TableOfContent